"use client";

import React from "react";
import { motion } from "framer-motion";
import { ShoppingBag, Building2, MapPin, Laptop, Compass, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const audienceItems = [{
  icon: ShoppingBag,
  title: "UK E-commerce Brands",
  description: "Retailers and digital store owners shipping products nationwide that need to dominate high-intent product category searches across the UK."
}, {
  icon: Building2,
  title: "B2B Service Providers",
  description: "Firms providing IT support, HR consultancy, corporate finance, or enterprise software targeting corporate clients throughout England, Scotland, Wales, and Northern Ireland."
}, {
  icon: MapPin,
  title: "Multi-Location Enterprises",
  description: "Businesses with regional offices or outlets seeking a strong, overarching nationwide brand presence alongside focused regional visibility."
}, {
  icon: Laptop,
  title: "SaaS & Digital Products",
  description: "Online-first platforms, web applications, and membership portals requiring steady organic acquisition for national users without location boundaries."
}, {
  icon: Compass,
  title: "Nationwide Professional Services",
  description: "Specialist firms in logistics, executive recruitment, commercial law, or management consulting competing for high-value contracts across the entire country."
}];
export default function WhatIsNationalSEO() {
  return <section id="whatisnationalseo" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 overflow-hidden relative">
      <div className="max-w-7xl mx-auto space-y-16">
        {/* Editorial Asymmetric Intro Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-5 space-y-4">
            <span className="text-primary font-semibold text-sm tracking-wider uppercase font-sans">
              National Search Strategy
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-serif tracking-tight text-foreground leading-tight">
              What Is National SEO &amp; Who Benefits From It
            </h2>
          </div>

          <div className="lg:col-span-7 space-y-6 text-foreground/90 font-sans text-base sm:text-lg leading-relaxed pt-2">
            <p>
              When I design a national SEO strategy for your business, my primary goal is simple: optimising your site so you rank prominently for commercially valuable, non-location-specific keywords across the entire country rather than being confined to a single city or region.
            </p>
            <p>
              Rather than competing solely in local maps or regional listings, national SEO targets broader, topic-focused search queries alongside high-intent transactional terms. As your independent consultant, I work directly with you to capture demand at every stage of the UK buyer journey, driving qualified organic traffic that converts into national clients and sales.
            </p>
          </div>
        </motion.div>

        {/* Section Breakdown: Card Grid */}
        <div className="space-y-8 pt-4">
          <motion.div initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="border-b border-border pb-4">
            <h3 className="text-2xl sm:text-3xl font-semibold font-serif text-foreground">
              Who National SEO Is Right For
            </h3>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {audienceItems.map((item, i) => {
            const Icon = item.icon;
            return <motion.div key={i} data-index={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} whileHover={{
              y: -4
            }}>
                  <Card className="h-full border border-border bg-card shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between rounded-lg">
                    <CardHeader className="space-y-4">
                      <div className="size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <Icon className="size-6 text-primary" aria-hidden="true" />
                      </div>
                      <CardTitle className="text-xl font-serif font-semibold text-foreground">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground font-sans text-sm sm:text-base leading-relaxed">
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}

            {/* Strategic Advice Highlight Banner in Grid position or spanning */}
            <motion.div data-index={5} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.5
          }} className="md:col-span-2 lg:col-span-1">
              <div className="h-full border border-accent/30 bg-secondary/40 p-6 rounded-lg flex flex-col justify-between space-y-4">
                <div className="space-y-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-accent-foreground/80 bg-accent/20 px-2.5 py-1 rounded-sm inline-block">
                    Consultant Advice
                  </span>
                  <h4 className="text-lg font-serif font-semibold text-foreground">
                    Starting Local First?
                  </h4>
                  <p className="text-foreground/80 font-sans text-sm leading-relaxed">
                    If you have a brand-new website, an unproven offer, or operate within a strictly local physical footprint, your growth is usually better served by starting with a targeted Local SEO strategy before scaling nationwide.
                  </p>
                </div>

                <Link to="/LocalSEOPage" className="inline-flex items-center gap-2 font-medium text-sm text-primary hover:text-primary/80 transition-colors pt-2 group">
                  <span>Explore Local SEO consultancy</span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}